import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChatUnread } from '../contexts/ChatUnreadContext';
import { Calendar, Users, Music, LayoutDashboard, Settings, LogOut, MessageSquare, ListMusic } from 'lucide-react';
import { getPermissionLabel } from '../utils/permissions';
import NotificationBell from './NotificationBell';

const NAV_ITEMS = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Services', path: '/services', icon: Calendar },
  { name: 'Songs', path: '/songs', icon: Music },
  { name: 'People', path: '/people', icon: Users },
  { name: 'Chat', path: '/chat', icon: MessageSquare },
];

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { totalUnread } = useChatUnread();
  const [profileOpen, setProfileOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* ── Top bar: logo + notification + profile ── */}
      <nav
        className="bg-white/90 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-700/60 sticky top-0 z-50"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        data-testid="top-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2.5 shrink-0"
              data-testid="nav-logo"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_f1fe9a42-12b3-4cb4-b6d2-85b551503a7f/artifacts/hqgml9p8_ncc%20logo.jpg"
                alt="NCC Logo"
                className="w-8 h-8 rounded-lg object-cover"
              />
              <span className="hidden sm:block text-sm font-bold text-green-700 tracking-widest">
                NCC
              </span>
            </button>

            {/* Right side: notifications + profile */}
            <div className="flex items-center gap-1">
              <NotificationBell />
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  data-testid="profile-menu-btn"
                  className="flex items-center p-1.5 rounded-full hover:bg-slate-100 transition-colors"
                >
                  {user?.picture && !user.picture.includes('placeholder') ? (
                    <img
                      src={user.picture.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${user.picture}` : user.picture}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-semibold">
                      {(user?.name || user?.email || '?')[0].toUpperCase()}
                    </div>
                  )}
                </button>

                {profileOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50"
                    data-testid="profile-dropdown"
                  >
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{user?.email}</p>
                      <p className="text-xs text-green-600 mt-0.5">{getPermissionLabel(user?.permission_level)}</p>
                    </div>
                    <button
                      onClick={() => { setProfileOpen(false); navigate('/lineups'); }}
                      data-testid="nav-lineups"
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <ListMusic className="w-4 h-4" /> Lineups
                    </button>
                    <button
                      onClick={() => { setProfileOpen(false); navigate('/settings'); }}
                      data-testid="nav-settings"
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Settings className="w-4 h-4" /> Settings
                    </button>
                    <button
                      onClick={() => { setProfileOpen(false); logout(); navigate('/'); }}
                      data-testid="logout-btn"
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Bottom nav: Dashboard, Services, Songs, People, Chat ── */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-1px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_-1px_12px_rgba(0,0,0,0.4)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="bottom-nav"
      >
        <div className="flex items-stretch max-w-lg mx-auto">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors"
              >
                <span className={`relative flex items-center justify-center w-9 h-7 rounded-full transition-all duration-200 ${
                  isActive ? 'bg-green-50 dark:bg-green-500/15' : ''
                }`}>
                  <Icon
                    className={`w-[18px] h-[18px] transition-colors duration-200 ${
                      isActive ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'
                    }`}
                    strokeWidth={isActive ? 2.25 : 1.75}
                  />
                  {item.name === 'Chat' && totalUnread > 0 && (
                    <span
                      data-testid="chat-unread-badge"
                      className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 leading-none"
                    >
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                </span>
                <span className={`text-[10px] font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'
                }`}>
                  {item.name}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
